import { EMAIL_TEMPLATE_REQUIRED_URL_VARIABLE, EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME } from "@constants/email-template-constants";
import { resources } from "@constants/hardcoded-strings";
import { ErrorAlert } from "@containers/common/error-alert/error-alert";
import { TabTitleWithErrorCount } from "@containers/common/tab-title-with-error-count/tab-title-with-error-count";
import { TemplateAvailabilityLevelTypes } from "@enums/template-availability-level-types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import {
    EmailTemplateContentValidationErrors,
    EmailTemplateGeneralInfoValidationErrors,
    EmailTemplateValidationErrors,
    EmailTemplateVariablesMultiLanguageValidationErrors,
    EmailTemplateVariablesValidationErrors
} from "@idw-types/common/validation/email-template/email-template-validation-errors";
import { FormattedEmailTemplate } from "@idw-types/data/email-template";
import { getEmailTemplateVariables } from "@utils/email-template/email-template-util";
import { isNullOrUndefined } from "@utils/object-util";
import { convertJSONObjectToDotSeparatedString } from "@utils/string-util";
import { getErrorAlert } from "@utils/tab-util";


export const getEmailTemplateValidationErrors = (emailTemplate: FormattedEmailTemplate): EmailTemplateValidationErrors => {
    const generalInfoErrors = getEmailTemplateGeneralInfoValidationErrors(emailTemplate);
    const contentErrors = getEmailTemplateContentValidationErrors(emailTemplate);
    const variablesErrors = getEmailTemplateVariablesValidationErrors(emailTemplate);

    const count = generalInfoErrors.count + contentErrors.count + variablesErrors.count;

    return {
        count,
        generalInfo: generalInfoErrors,
        variables: variablesErrors,
        content: contentErrors,
        contentAndVariables: {
            count: variablesErrors.count,
            tabTitle: <TabTitleWithErrorCount tabName={resources.pages.emailTemplateDetails.tabs.content.tabName} errorCount={variablesErrors.count} />
        }
    };
};
export const getEmailTemplateContentValidationErrors = (emailTemplate: FormattedEmailTemplate): EmailTemplateContentValidationErrors => {
    let count = 0;
    let requiredVariableError: React.ReactNode = null;

    if (!emailTemplate?.template?.includes(EMAIL_TEMPLATE_REQUIRED_URL_VARIABLE)) {
        count++;
        requiredVariableError = (
            <ErrorAlert id={`email-template-required-variable-error`}>
                <>
                    <div>{`The following text must appear in your template:`}</div>
                    <p>{EMAIL_TEMPLATE_REQUIRED_URL_VARIABLE}</p>
                    <div>{`This text will be substituted in the message sent to the user. The user will use the substituted URL.`}</div>
                </>
            </ErrorAlert>
        );
    }
    return {
        count,
        requiredVariableError,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.emailTemplateDetails.tabs.content.tabName} errorCount={count} />
    };
};

const getEmailTemplateGeneralInfoValidationErrors = (emailTemplate: FormattedEmailTemplate): EmailTemplateGeneralInfoValidationErrors => {
    let nameError: React.ReactNode = null;
    let senderError: React.ReactNode = null;
    let count = 0;
    let companyError = null;
    let templateAvailabilityLevelError = null;
    const { templateAvailabilityLevel, company } = emailTemplate;

    if (!emailTemplate.name) {
        count++;
        nameError = getErrorAlert(resources.common.requiredField);
    }

    if (templateAvailabilityLevel === null) {
        templateAvailabilityLevelError = getErrorAlert(resources.common.requiredField);
        count++;
    } else {
        if (templateAvailabilityLevel === TemplateAvailabilityLevelTypes.ORGANIZATION && company === null) {
            companyError = getErrorAlert(resources.common.requiredField);
            count++;
        }
    }
    if (emailTemplate.sender === "") {
        count += 1;
        senderError = (
            <ErrorAlert>
                <span>{resources.pages.emailTemplateDetails.tabs.variables.defaultSender.error}</span>
            </ErrorAlert>
        );
    }

    return {
        count,
        nameError,
        templateAvailabilityLevelError,
        companyError,
        senderError,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.emailTemplateDetails.tabs.generalInfo.tabName} errorCount={count} />
    };
};


export const getEmailTemplateVariablesValidationErrors = (emailTemplate: FormattedEmailTemplate): EmailTemplateVariablesValidationErrors => {
    let count = 0;
    const multiLanguageValidationErrors: EmailTemplateVariablesMultiLanguageValidationErrors = {};

    const availableLanguages = Object.keys(emailTemplate.languages) as Array<LocaleTypes>;
    availableLanguages.forEach(locale => {
        let flattenedLanguageFile: { [key: string]: string } = {};
        if (emailTemplate.languages[locale]) {
            flattenedLanguageFile = convertJSONObjectToDotSeparatedString(emailTemplate.languages[locale]);
        }
        const finalVariables: Array<{ name: string; value: string; }> = getEmailTemplateVariables(emailTemplate.languages, emailTemplate.template, locale);


        multiLanguageValidationErrors[locale] = { errors: {}, count: 0 };

        finalVariables.forEach(variable => {
            if (!variable.value) {
                multiLanguageValidationErrors[locale].errors[variable.name] = getErrorAlert(resources.common.requiredField, `required-variable-${variable.name}`);
                multiLanguageValidationErrors[locale].count += 1;
                count++;
            }
        });

    if (isNullOrUndefined(flattenedLanguageFile[EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME]) || flattenedLanguageFile[EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME] === "" ) {
            multiLanguageValidationErrors[locale].errors[EMAIL_TEMPLATE_SUBJECT_VARIABLE_NAME] = getErrorAlert(resources.common.requiredField, `required-variable-email.subject`);
            multiLanguageValidationErrors[locale].count += 1;
            count++;
        }
    });


    return {
        count: count,
        multiLanguageValidationErrors,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.emailTemplateDetails.tabs.variables.tabName} errorCount={count} />
    };
};